import { useEffect, useRef, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import styles from './ActionSheet.module.css'
import type { ActionSheetAction, ActionSheetOptions } from './action-sheet-store.ts'
import { closeActionSheet } from './action-sheet-store.ts'

type ActionSheetBodyProps = {
  options: Pick<ActionSheetOptions, 'title' | 'description' | 'inset' | 'actions'>
  content: ReactNode
  onActionClose?: () => void
}

type ActionSheetActionsProps = {
  actions: ActionSheetAction[]
  onClose: () => void
}

const KEYBOARD_CLOSE_GROWTH_PX = 50

function ActionSheetActions({ actions, onClose }: ActionSheetActionsProps) {
  return (
    <div className={styles.actions} role="group">
      {actions.map((action) => (
        <button
          key={action.label}
          type="button"
          className={cn(styles.action, {
            [styles['action--destructive']]: action.destructive,
          })}
          disabled={action.disabled}
          onClick={() => {
            if (action.disabled) {
              return
            }
            action.onSelect()
            onClose()
          }}
        >
          {action.label}
        </button>
      ))}
    </div>
  )
}

export function ActionSheetBody({ options, content, onActionClose = closeActionSheet }: ActionSheetBodyProps) {
  const bodyRef = useRef<HTMLDivElement>(null)
  const hasActions = Boolean(options.actions?.length)

  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) {
      return
    }

    let previousHeight = viewport.height

    const resetScroll = () => {
      const body = bodyRef.current
      if (body && body.scrollTop > 0) {
        body.scrollTop = 0
      }
    }

    const handleResize = () => {
      const nextHeight = viewport.height
      const grew = nextHeight > previousHeight + KEYBOARD_CLOSE_GROWTH_PX
      previousHeight = nextHeight
      if (!grew) {
        return
      }
      resetScroll()
      requestAnimationFrame(resetScroll)
    }

    viewport.addEventListener('resize', handleResize)
    return () => viewport.removeEventListener('resize', handleResize)
  }, [])

  return (
    <div
      ref={bodyRef}
      className={cn(styles.body, {
        [styles['body--inset']]: options.inset,
      })}
    >
      {options.title && <h2 className={styles.title}>{options.title}</h2>}
      {options.description && <p className={styles.description}>{options.description}</p>}
      {content && (
        <div
          className={cn(styles.slot, {
            [styles['slot--inset']]: options.inset,
          })}
        >
          {content}
        </div>
      )}
      {hasActions && <ActionSheetActions actions={options.actions ?? []} onClose={onActionClose} />}
    </div>
  )
}
